import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    capitalize,
    Chip,
    CircularProgress,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { ArrowForward } from "@mui/icons-material";
import { getNeonColor, scrollable } from "../../theme";
import { useReferentialContext } from "../../Context";
import { Maps, ScrimGames, ScrimGameSummaries, ScrimTeamRoundSummaries } from "../../reckon-api";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import { NeonButton } from "../../NeonComponents/Buttons";
import formatters from "../../NeonComponents/Formatters";
import TeamIcon from "../../components/Icons/TeamIcon";
import AgentIcon from "../../components/Icons/AgentIcon";
import WindowContainer from "../../components/layout/WindowContainer";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import { getMapAsset } from "../../valorant-api/mapAssets";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NormalizedGame {
    id: string;
    date: string;
    map: string;
    myScore: number;
    enemyScore: number;
    won: boolean;
    opponent: string;
}

interface GameRoundBreakdown {
    atkWon: number;
    atkLost: number;
    defWon: number;
    defLost: number;
    /** Always 2 slots: first pistol, second pistol. null = unknown/missing */
    pistolResults: [boolean | null, boolean | null];
}

interface ScrimSession {
    dateLabel: string;
    date: string;
    opponent: string;
    games: NormalizedGame[];
    wins: number;
    losses: number;
}

interface MapRateStat {
    rate: number | null;
    wins: number | null;
    losses: number | null;
}

interface MapStats {
    map: string;
    games: number;
    wins: number;
    losses: number;
    winRate: number;
    round: MapRateStat;
    pistol: MapRateStat;
    atk: MapRateStat;
    def: MapRateStat;
}

interface PlayerAgg {
    player: string;
    igName: string;
    games: number;
    wins: number;
    kills: number;
    deaths: number;
    assists: number;
    acs: number;
    adr: number;
    kast: number;
    rating: number;
    hs: number;
    firstKills: number;
    firstDeaths: number;
    agents: Map<string, number>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function teamLabel(teamId: string) {
    return teamId?.split("_")[0] || teamId;
}

function selectRecentGamesForDisplay(games: NormalizedGame[], targetUnits = 6): NormalizedGame[] {
    // Day headers take space (~0.65 of a game row); keep total visual weight near the old 6-row budget.
    const selected: NormalizedGame[] = [];
    let units = 0;
    let prevDay: string | null = null;

    for (const game of games) {
        const day = dayjs(game.date).format("YYYY-MM-DD");
        const needsDivider = prevDay === null || prevDay !== day;
        const cost = (needsDivider ? 0.65 : 0) + 1;
        if (selected.length > 0 && units + cost > targetUnits) break;
        units += cost;
        selected.push(game);
        prevDay = day;
    }

    return selected;
}

function buildGameRoundBreakdowns(
    rounds: ScrimTeamRoundSummaries[],
    userTeam: string,
): Map<string, GameRoundBreakdown> {
    const byGame = new Map<string, ScrimTeamRoundSummaries[]>();
    for (const r of rounds) {
        // Prefer team1 match; fall back to players string like history view
        const isMine =
            r.team1 === userTeam ||
            (r.players || "").toLowerCase().includes(teamLabel(userTeam).toLowerCase());
        if (!isMine) continue;
        const list = byGame.get(r.game) || [];
        list.push(r);
        byGame.set(r.game, list);
    }

    const out = new Map<string, GameRoundBreakdown>();
    for (const [gameId, gameRounds] of byGame) {
        const sorted = [...gameRounds].sort((a, b) => a.round_index - b.round_index);
        let atkWon = 0;
        let atkLost = 0;
        let defWon = 0;
        let defLost = 0;
        let firstPistol: boolean | null = null;
        let secondPistol: boolean | null = null;

        for (const r of sorted) {
            const won = r.win === 1;
            if (r.side === "Attacker") {
                if (won) atkWon += 1;
                else atkLost += 1;
            } else if (r.side === "Defender") {
                if (won) defWon += 1;
                else defLost += 1;
            }

            const idx = r.round_index;
            const eco = `${r.round_eco_type_no_bonus || ""} ${r.round_eco_type || ""}`.toLowerCase();
            const isPistolEco = eco.includes("pistol");
            // Support both 0-based (0,12) and 1-based (1,13) round indexes
            if (idx === 0 || idx === 1 || (isPistolEco && firstPistol === null && idx < 2)) {
                firstPistol = won;
            } else if (idx === 12 || idx === 13 || (isPistolEco && idx >= 12 && idx <= 14)) {
                secondPistol = won;
            }
        }

        out.set(gameId, {
            atkWon,
            atkLost,
            defWon,
            defLost,
            pistolResults: [firstPistol, secondPistol],
        });
    }
    return out;
}

function normalizeGames(scrims: ScrimGames[], userTeam: string): NormalizedGame[] {
    return scrims
        .filter((g) => g.team1 === userTeam || g.team2 === userTeam)
        .map((g) => {
            const isTeam1 = g.team1 === userTeam;
            return {
                id: g.id,
                date: g.date,
                map: g.map,
                myScore: isTeam1 ? g.score_team1 : g.score_team2,
                enemyScore: isTeam1 ? g.score_team2 : g.score_team1,
                won: g.winner === userTeam,
                opponent: isTeam1 ? g.team2 : g.team1,
            };
        })
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}

function buildSessions(games: NormalizedGame[]): ScrimSession[] {
    const grouped = new Map<string, NormalizedGame[]>();
    for (const game of games) {
        const dayKey = dayjs(game.date).format("YYYY-MM-DD");
        const key = `${dayKey}__${game.opponent}`;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(game);
    }

    const sessions: ScrimSession[] = [];
    for (const [, sessionGames] of grouped) {
        const sorted = [...sessionGames].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
        const wins = sorted.filter((g) => g.won).length;
        sessions.push({
            dateLabel: dayjs(sorted[0].date).format("ddd, MMM D"),
            date: dayjs(sorted[0].date).format("YYYY-MM-DD"),
            opponent: sorted[0].opponent,
            games: sorted,
            wins,
            losses: sorted.length - wins,
        });
    }

    return sessions.sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
}

const EMPTY_RATE: MapRateStat = { rate: null, wins: null, losses: null };

function buildMapStats(games: NormalizedGame[]): MapStats[] {
    const byMap = new Map<string, { games: number; wins: number }>();
    for (const g of games) {
        const cur = byMap.get(g.map) || { games: 0, wins: 0 };
        cur.games += 1;
        if (g.won) cur.wins += 1;
        byMap.set(g.map, cur);
    }
    return Array.from(byMap.entries())
        .map(([map, s]) => ({
            map,
            games: s.games,
            wins: s.wins,
            losses: s.games - s.wins,
            winRate: s.games ? s.wins / s.games : 0,
            round: EMPTY_RATE,
            pistol: EMPTY_RATE,
            atk: EMPTY_RATE,
            def: EMPTY_RATE,
        }))
        .sort((a, b) => b.games - a.games || b.winRate - a.winRate);
}

function mapRateStatsFromRows(rows: any[]): Map<string, MapRateStat> {
    const out = new Map<string, MapRateStat>();
    for (const row of rows) {
        const map = row?.map;
        const rateRaw = row?.win__avg;
        if (!map || rateRaw === null || rateRaw === undefined || Number.isNaN(Number(rateRaw))) continue;
        const rate = Number(rateRaw);
        let rounds = Number(row?.rounds) || 0;
        // scrimRoundSummaries aggregates player-rounds; normalize to team rounds
        if (rounds >= 5 && rounds % 5 === 0) rounds = rounds / 5;
        const wins = rounds > 0 ? Math.round(rate * rounds) : null;
        const losses = wins === null ? null : Math.max(0, rounds - wins);
        out.set(String(map).toLowerCase(), { rate, wins, losses });
    }
    return out;
}

function mergeMapRoundRates(
    base: MapStats[],
    round: Map<string, MapRateStat>,
    pistol: Map<string, MapRateStat>,
    atk: Map<string, MapRateStat>,
    def: Map<string, MapRateStat>,
): MapStats[] {
    return base.map((m) => {
        const key = m.map.toLowerCase();
        return {
            ...m,
            round: round.get(key) ?? EMPTY_RATE,
            pistol: pistol.get(key) ?? EMPTY_RATE,
            atk: atk.get(key) ?? EMPTY_RATE,
            def: def.get(key) ?? EMPTY_RATE,
        };
    });
}

function rateColor(rate: number | null, neonColor: string) {
    if (rate === null) return "rgba(255,255,255,0.45)";
    if (rate >= 0.55) return "#81c784";
    if (rate < 0.45) return "#e57373";
    return neonColor;
}

function aggregatePlayers(
    summaries: ScrimGameSummaries[],
    gamesById: Map<string, NormalizedGame>,
): PlayerAgg[] {
    const byPlayer = new Map<string, PlayerAgg>();

    for (const s of summaries) {
        const game = gamesById.get(s.game_id);
        if (!game) continue;
        const onMyTeam = s.win === game.won;
        if (!onMyTeam) continue;

        // Group by linked player id — never by riot account / ig_name
        const playerId = s.player;
        if (!playerId) continue;

        let agg = byPlayer.get(playerId);
        if (!agg) {
            agg = {
                player: playerId,
                igName: s.ig_name,
                games: 0,
                wins: 0,
                kills: 0,
                deaths: 0,
                assists: 0,
                acs: 0,
                adr: 0,
                kast: 0,
                rating: 0,
                hs: 0,
                firstKills: 0,
                firstDeaths: 0,
                agents: new Map(),
            };
            byPlayer.set(playerId, agg);
        }

        agg.games += 1;
        if (s.win) agg.wins += 1;
        agg.kills += s.kills ?? 0;
        agg.deaths += s.deaths ?? 0;
        agg.assists += s.assists ?? 0;
        agg.acs += s.acs ?? 0;
        agg.adr += s.adr ?? 0;
        agg.kast += s.kast ?? 0;
        agg.rating += s.rating_hltv ?? 0;
        agg.hs += s.headshot_percent ?? 0;
        agg.firstKills += s.first_kills ?? 0;
        agg.firstDeaths += s.first_deaths ?? 0;
        if (s.agent) agg.agents.set(s.agent, (agg.agents.get(s.agent) || 0) + 1);
        if (s.ig_name) agg.igName = s.ig_name;
    }

    return Array.from(byPlayer.values())
        .map((p) => ({
            ...p,
            acs: p.games ? p.acs / p.games : 0,
            adr: p.games ? p.adr / p.games : 0,
            kast: p.games ? p.kast / p.games : 0,
            rating: p.games ? p.rating / p.games : 0,
            hs: p.games ? p.hs / p.games : 0,
        }))
        .sort((a, b) => b.rating - a.rating || b.acs - a.acs);
}

function topAgents(agents: Map<string, number>, n = 3): string[] {
    return Array.from(agents.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([agent]) => agent);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function KpiTile({
    label,
    value,
    accent,
}: {
    label: string;
    value: string;
    accent?: string;
}) {
    return (
        <Box sx={{ textAlign: "center", px: 1, py: 0.5 }}>
            <Typography
                sx={{
                    fontWeight: 800,
                    fontSize: "1.45rem",
                    lineHeight: 1.15,
                    color: accent || "common.white",
                }}
            >
                {value}
            </Typography>
            <Typography
                sx={{
                    mt: 0.25,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.5)",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

function SideScoreChip({
    label,
    won,
    lost,
}: {
    label: string;
    won: number;
    lost: number;
}) {
    return (
        <Stack
            direction="row"
            spacing={0.4}
            sx={{
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: 22,
                px: 0.5,
                borderRadius: 0.75,
                backgroundColor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    width: 24,
                    flexShrink: 0,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: "#81c784",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 14,
                    textAlign: "right",
                }}
            >
                {won}
            </Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.4)" }}>-</Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: "#e57373",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 14,
                    textAlign: "left",
                }}
            >
                {lost}
            </Typography>
        </Stack>
    );
}

function PistolResultsChip({ results }: { results: [boolean | null, boolean | null] }) {
    return (
        <Stack
            direction="row"
            spacing={0.4}
            sx={{
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: 22,
                px: 0.5,
                borderRadius: 0.75,
                backgroundColor: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.12)",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "rgba(255,255,255,0.55)",
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    width: 24,
                    flexShrink: 0,
                }}
            >
                PIS
            </Typography>
            {results.map((won, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "3px",
                        backgroundColor:
                            won === null
                                ? "rgba(255,255,255,0.08)"
                                : won
                                  ? "rgba(76,175,80,0.35)"
                                  : "rgba(244,67,54,0.35)",
                        border: `1px solid ${
                            won === null
                                ? "rgba(255,255,255,0.2)"
                                : won
                                  ? "rgba(129,199,132,0.8)"
                                  : "rgba(229,115,115,0.8)"
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.55rem",
                            fontWeight: 800,
                            color:
                                won === null ? "rgba(255,255,255,0.35)" : won ? "#81c784" : "#e57373",
                            lineHeight: 1,
                        }}
                    >
                        {won === null ? "·" : won ? "W" : "L"}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
}

function GameRow({
    game,
    userTeam,
    onOpen,
    mapsById,
    breakdown,
}: {
    game: NormalizedGame;
    userTeam: string;
    onOpen: () => void;
    mapsById: Map<string, Maps>;
    breakdown?: GameRoundBreakdown;
}) {
    const accent = game.won ? "#4caf50" : "#f44336";
    const dateLabel = dayjs(game.date).format("ddd, MMM D");

    return (
        <Box
            onClick={onOpen}
            sx={{
                position: "relative",
                width: "100%",
                flexShrink: 0,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                borderLeft: `3px solid ${accent}`,
                transition: "filter 0.2s, box-shadow 0.2s",
                "&:hover": {
                    filter: "brightness(1.12)",
                    boxShadow: `0 0 16px ${accent}33`,
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${mapSplashUrl(game.map, mapsById.get(game.map.toLowerCase()))})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center 35%",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: game.won
                        ? "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(8,18,8,0.82) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.28) 100%)"
                        : "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(22,8,8,0.82) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.28) 100%)",
                }}
            />

            <Stack
                direction="row"
                spacing={1.25}
                sx={{
                    position: "relative",
                    zIndex: 2,
                    width: "100%",
                    alignItems: "center",
                    px: 1.5,
                    py: 1.25,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: "rgba(255,255,255,0.7)",
                        minWidth: 72,
                        fontSize: "0.75rem",
                        textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    }}
                >
                    {dateLabel}
                </Typography>

                <TeamIcon team_name={userTeam} sx={{ width: 26, height: 26 }} />
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 52, justifyContent: "center" }}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: game.won ? "lightgreen" : "error.main",
                            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        {game.myScore}
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>-</Typography>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: game.won ? "error.main" : "lightgreen",
                            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        {game.enemyScore}
                    </Typography>
                </Stack>
                <TeamIcon team_name={game.opponent} sx={{ width: 26, height: 26, flexShrink: 0 }} />
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        width: 100,
                        flexShrink: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    }}
                >
                    {teamLabel(game.opponent)}
                </Typography>

                <Box sx={{ flex: 1, minWidth: 8 }} />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "72px 72px 68px 72px",
                        gap: 0.75,
                        alignItems: "center",
                        flexShrink: 0,
                    }}
                >
                    <SideScoreChip
                        label="ATK"
                        won={breakdown?.atkWon ?? 0}
                        lost={breakdown?.atkLost ?? 0}
                    />
                    <SideScoreChip
                        label="DEF"
                        won={breakdown?.defWon ?? 0}
                        lost={breakdown?.defLost ?? 0}
                    />
                    <PistolResultsChip results={breakdown?.pistolResults ?? [null, null]} />
                    <Chip
                        label={capitalize(game.map)}
                        size="small"
                        variant="outlined"
                        sx={{
                            width: "100%",
                            fontSize: "0.65rem",
                            height: 22,
                            borderColor: game.won ? "rgba(76,175,80,0.55)" : "rgba(244,67,54,0.55)",
                            color: game.won ? "#81c784" : "#e57373",
                            backgroundColor: "rgba(0,0,0,0.45)",
                            backdropFilter: "blur(4px)",
                            "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                px: 0.75,
                            },
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
}

function mapSplashUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.uuid) {
        return `https://media.valorant-api.com/maps/${mapRef.uuid}/splash.png`;
    }
    return getMapAsset(mapId, "splash").replace("/upload/", "/upload/c_fill,h_160,w_480/");
}

function mapBannerUrl(mapId: string, mapRef?: Maps) {
    if (mapRef?.uuid) {
        return `https://media.valorant-api.com/maps/${mapRef.uuid}/listviewicon.png`;
    }
    return getMapAsset(mapId, "listViewIcon").replace("/upload/", "/upload/c_fill,h_72,w_200/");
}

function mapGridDims(count: number): { cols: number; rows: number } {
    if (count <= 1) return { cols: 1, rows: 1 };
    // Near-square grid that fits every map (2→2x1, 4→2x2, 5→3x2, 9→3x3, …)
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    return { cols, rows };
}

function MapStatCard({
    stats,
    neonColor,
    mapRef,
    selected,
    onSelect,
}: {
    stats: MapStats;
    neonColor: string;
    mapRef?: Maps;
    selected: boolean;
    onSelect: () => void;
}) {
    const barColor =
        stats.winRate >= 0.55 ? "#4caf50" : stats.winRate >= 0.45 ? neonColor : "#f44336";
    const [imgSrc, setImgSrc] = useState(mapSplashUrl(stats.map, mapRef));

    useEffect(() => {
        setImgSrc(mapSplashUrl(stats.map, mapRef));
    }, [stats.map, mapRef?.uuid]);

    return (
        <Box
            onClick={onSelect}
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: 0,
                minWidth: 0,
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                border: selected ? `2px solid ${neonColor}` : "1px solid rgba(255,255,255,0.12)",
                boxShadow: selected ? `0 0 18px ${neonColor}55, inset 0 0 24px ${neonColor}22` : "none",
                backgroundColor: "rgba(255,255,255,0.03)",
                transition: "border-color 0.2s, box-shadow 0.2s, filter 0.2s",
                "&:hover": {
                    filter: "brightness(1.08)",
                    borderColor: selected ? neonColor : "rgba(255,255,255,0.28)",
                },
            }}
        >
            <Box
                component="img"
                src={imgSrc}
                alt={stats.map}
                onError={() => setImgSrc(mapBannerUrl(stats.map, mapRef))}
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.96) 100%)",
                }}
            />
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    height: "100%",
                    justifyContent: "flex-end",
                    p: 0.75,
                    gap: 0.35,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "clamp(0.65rem, 1.4vw, 0.9rem)",
                        textTransform: "capitalize",
                        letterSpacing: 0.15,
                        lineHeight: 1.1,
                        textShadow: "0 1px 8px rgba(0,0,0,0.9)",
                    }}
                >
                    {stats.map}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "56px minmax(56px, 1fr) 48px",
                        columnGap: 0.75,
                        alignItems: "center",
                    }}
                >
                    <Typography sx={{ fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                        Games
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)",
                            color: "rgba(255,255,255,0.7)",
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                        }}
                    >
                        {stats.wins}W–{stats.losses}L
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "clamp(0.6rem, 1.2vw, 0.8rem)",
                            color: barColor,
                            textShadow: `0 0 10px ${barColor}66`,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                        }}
                    >
                        {formatters.percent(stats.winRate)}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.max(stats.winRate * 100, 4)}
                    sx={{
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.12)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 2,
                            backgroundColor: barColor,
                            boxShadow: `0 0 8px ${barColor}88`,
                        },
                    }}
                />
                {(
                    [
                        ["Round", stats.round],
                        ["Pistol", stats.pistol],
                        ["Atk", stats.atk],
                        ["Def", stats.def],
                    ] as const
                ).map(([label, detail]) => {
                    const rate = detail.rate;
                    const color = rateColor(rate, neonColor);
                    const record =
                        detail.wins !== null && detail.losses !== null
                            ? `${detail.wins}W–${detail.losses}L`
                            : "—";
                    return (
                        <Box key={label}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "56px minmax(56px, 1fr) 48px",
                                    columnGap: 0.75,
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)", color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "clamp(0.55rem, 1.1vw, 0.7rem)",
                                        color: "rgba(255,255,255,0.55)",
                                        fontWeight: 600,
                                        fontVariantNumeric: "tabular-nums",
                                        textAlign: "right",
                                    }}
                                >
                                    {record}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "clamp(0.6rem, 1.2vw, 0.8rem)",
                                        color,
                                        textShadow: rate === null ? "none" : `0 0 10px ${color}66`,
                                        fontVariantNumeric: "tabular-nums",
                                        textAlign: "right",
                                    }}
                                >
                                    {rate === null ? "—" : formatters.percent(rate)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={rate === null ? 0 : Math.max(rate * 100, 4)}
                                sx={{
                                    mt: 0.2,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 2,
                                        backgroundColor: color,
                                        boxShadow: rate === null ? "none" : `0 0 8px ${color}88`,
                                    },
                                }}
                            />
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

function PlayerStatsTable({ players }: { players: PlayerAgg[] }) {
    const headers = ["Player", "Agents", "G", "ACS", "KD", "ADR", "Rating", "FK/FD"];
    const colWidths = ["22%", "14%", "8%", "12%", "10%", "12%", "12%", "10%"];

    const thSx = {
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase" as const,
        color: "rgba(255,255,255,0.45)",
        py: 1,
        px: 0.5,
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        whiteSpace: "nowrap" as const,
    };
    const tdSx = {
        py: 0.85,
        px: 0.5,
        fontSize: "0.82rem",
        fontWeight: 600,
        color: "rgba(255,255,255,0.9)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
    };

    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box
                component="table"
                sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                }}
            >
                <Box component="colgroup">
                    {colWidths.map((w, i) => (
                        <Box component="col" key={headers[i]} sx={{ width: w }} />
                    ))}
                </Box>
                <Box component="thead">
                    <Box component="tr">
                        {headers.map((h, i) => (
                            <Box
                                component="th"
                                key={h}
                                sx={{
                                    ...thSx,
                                    textAlign: i < 2 ? "left" : "center",
                                }}
                            >
                                {h}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {players.map((player) => {
                        const kd = player.deaths ? player.kills / player.deaths : player.kills;
                        const agents = topAgents(player.agents, 2);
                        const ratingColor =
                            player.rating >= 1
                                ? "#81c784"
                                : player.rating > 0
                                  ? "rgba(255,255,255,0.9)"
                                  : "rgba(255,255,255,0.4)";

                        return (
                            <Box
                                component="tr"
                                key={player.player}
                                sx={{
                                    "&:hover td": { backgroundColor: "rgba(255,255,255,0.03)" },
                                    "&:last-child td": { borderBottom: "none" },
                                }}
                            >
                                <Box
                                    component="td"
                                    sx={{
                                        ...tdSx,
                                        textAlign: "left",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: 700,
                                    }}
                                    title={player.igName || undefined}
                                >
                                    {player.player}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "left" }}>
                                    <Stack direction="row" spacing={0.4} sx={{ alignItems: "center", height: 24 }}>
                                        {agents.map((agent) => (
                                            <AgentIcon key={agent} agent_name={agent} sx={{ width: 20, height: 20 }} />
                                        ))}
                                    </Stack>
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {player.games}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {formatters.float1(player.acs)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {formatters.float2(kd)}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {formatters.float1(player.adr)}
                                </Box>
                                <Box
                                    component="td"
                                    sx={{ ...tdSx, textAlign: "center", color: ratingColor, fontWeight: 700 }}
                                >
                                    {player.rating ? formatters.float2(player.rating) : "—"}
                                </Box>
                                <Box component="td" sx={{ ...tdSx, textAlign: "center" }}>
                                    {player.firstKills}/{player.firstDeaths}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ScrimsDashboard() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const navigate = useNavigate();
    const userTeam = ctx.user.team;

    const [from, setFrom] = useState(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState(dayjs());
    const [games, setGames] = useState<ScrimGames[]>([]);
    const [summaries, setSummaries] = useState<ScrimGameSummaries[]>([]);
    const [gamesLoading, setGamesLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [mapRoundRates, setMapRoundRates] = useState<{
        round: Map<string, MapRateStat>;
        pistol: Map<string, MapRateStat>;
        atk: Map<string, MapRateStat>;
        def: Map<string, MapRateStat>;
    }>({
        round: new Map(),
        pistol: new Map(),
        atk: new Map(),
        def: new Map(),
    });
    const [gameBreakdowns, setGameBreakdowns] = useState<Map<string, GameRoundBreakdown>>(new Map());
    const [selectedMap, setSelectedMap] = useState<string | null>(null);

    useEffect(() => {
        setGamesLoading(true);
        ctx.reckonApi.scrimGames
            .list({
                ordering: ["-date"],
                additional_filters: {
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                },
            })
            .then((result) => {
                setGames(result);
                setGamesLoading(false);
            })
            .catch(() => setGamesLoading(false));
    }, [from, until]);

    const myGames = useMemo(() => normalizeGames(games, userTeam), [games, userTeam]);
    const sessions = useMemo(() => buildSessions(myGames), [myGames]);
    const baseMapStats = useMemo(() => buildMapStats(myGames), [myGames]);
    const mapStats = useMemo(
        () =>
            mergeMapRoundRates(
                baseMapStats,
                mapRoundRates.round,
                mapRoundRates.pistol,
                mapRoundRates.atk,
                mapRoundRates.def,
            ),
        [baseMapStats, mapRoundRates],
    );
    const mapGrid = useMemo(() => mapGridDims(mapStats.length), [mapStats.length]);
    const gamesById = useMemo(() => new Map(myGames.map((g) => [g.id, g])), [myGames]);
    const gameIdsKey = useMemo(() => myGames.map((g) => g.id).join(","), [myGames]);

    const filteredGames = useMemo(() => {
        if (!selectedMap) return myGames;
        return myGames.filter((g) => g.map.toLowerCase() === selectedMap.toLowerCase());
    }, [myGames, selectedMap]);

    const recentGames = useMemo(() => selectRecentGamesForDisplay(filteredGames), [filteredGames]);
    const recentGameIdsKey = useMemo(() => recentGames.map((g) => g.id).join(","), [recentGames]);
    const filteredGameIds = useMemo(() => new Set(filteredGames.map((g) => g.id)), [filteredGames]);

    // Drop selection if that map disappears from the current period
    useEffect(() => {
        if (!selectedMap) return;
        const stillPresent = mapStats.some((m) => m.map.toLowerCase() === selectedMap.toLowerCase());
        if (!stillPresent) setSelectedMap(null);
    }, [mapStats, selectedMap]);

    const kpis = useMemo(() => {
        const wins = myGames.filter((g) => g.won).length;
        const losses = myGames.length - wins;
        return {
            games: myGames.length,
            wins,
            losses,
            winRate: myGames.length ? wins / myGames.length : 0,
            maps: mapStats.length,
            sessions: sessions.length,
        };
    }, [myGames, mapStats, sessions]);

    const mapsById = useMemo(
        () => new Map(ctx.mapsReferential.map((m) => [m.id.toLowerCase(), m])),
        [ctx.mapsReferential],
    );

    useEffect(() => {
        if (!gameIdsKey) {
            setSummaries([]);
            setStatsLoading(false);
            return;
        }

        setStatsLoading(true);
        ctx.reckonApi.scrimGameSummaries
            .list({
                ordering: ["-acs"],
                additional_filters: {
                    game_id__in: gameIdsKey,
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                },
            })
            .then((result) => {
                setSummaries(result);
                setStatsLoading(false);
            })
            .catch(() => setStatsLoading(false));
    }, [gameIdsKey, from, until]);

    useEffect(() => {
        if (!gameIdsKey) {
            setMapRoundRates({
                round: new Map(),
                pistol: new Map(),
                atk: new Map(),
                def: new Map(),
            });
            return;
        }

        const base = {
            team1: userTeam,
            groupby: "map" as any,
            aggregates: ["win__avg"] as any,
            metrics: ["rounds"],
            ordering: ["-rounds"] as any,
            additional_filters: {
                date__gte: from.toISOString(),
                date__lt: until.toISOString(),
                game__in: gameIdsKey,
            },
        };

        let cancelled = false;
        Promise.all([
            ctx.reckonApi.scrimRoundSummaries.aggregate(base),
            ctx.reckonApi.scrimRoundSummaries.aggregate({
                ...base,
                additional_filters: {
                    ...base.additional_filters,
                    round_index__in: "1,13",
                },
            }),
            ctx.reckonApi.scrimRoundSummaries.aggregate({ ...base, side: "Attacker" }),
            ctx.reckonApi.scrimRoundSummaries.aggregate({ ...base, side: "Defender" }),
        ])
            .then(([roundRows, pistolRows, atkRows, defRows]) => {
                if (cancelled) return;
                setMapRoundRates({
                    round: mapRateStatsFromRows(roundRows),
                    pistol: mapRateStatsFromRows(pistolRows),
                    atk: mapRateStatsFromRows(atkRows),
                    def: mapRateStatsFromRows(defRows),
                });
            })
            .catch(() => {
                if (cancelled) return;
                setMapRoundRates({
                    round: new Map(),
                    pistol: new Map(),
                    atk: new Map(),
                    def: new Map(),
                });
            });

        return () => {
            cancelled = true;
        };
    }, [gameIdsKey, from, until, userTeam]);

    useEffect(() => {
        if (!recentGameIdsKey) {
            setGameBreakdowns(new Map());
            return;
        }

        let cancelled = false;
        ctx.reckonApi.scrimTeamRoundSummaries
            .list({
                team1: userTeam,
                ordering: ["round_index"],
                additional_filters: {
                    game__in: recentGameIdsKey,
                },
            })
            .then((rounds) => {
                if (cancelled) return;
                setGameBreakdowns(buildGameRoundBreakdowns(rounds, userTeam));
            })
            .catch(() => {
                if (cancelled) return;
                setGameBreakdowns(new Map());
            });

        return () => {
            cancelled = true;
        };
    }, [recentGameIdsKey, userTeam]);

    const players = useMemo(() => {
        const scopedSummaries = selectedMap
            ? summaries.filter((s) => filteredGameIds.has(s.game_id))
            : summaries;
        return aggregatePlayers(scopedSummaries, gamesById);
    }, [summaries, gamesById, selectedMap, filteredGameIds]);

    const loading = gamesLoading;

    return (
        <WindowContainer
            spacing={2}
            sx={{
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pb: 2,
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", flexShrink: 0 }}
            >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0, flex: 1 }}>
                    <TeamIcon team_name={userTeam} sx={{ width: 40, height: 40, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                            Scrim Dashboard
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {teamLabel(userTeam)} · overview of recent practice
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <DatePicker value={from} onChange={(v: Dayjs | null) => v && setFrom(v)} label="From" />
                    <DatePicker value={until} onChange={(v: Dayjs | null) => v && setUntil(v)} label="Until" />
                </Stack>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
                    <CircularProgress sx={{ color: neonColor }} />
                </Box>
            ) : myGames.length === 0 ? (
                <NeonCard>
                    <NeonCardContent>
                        <Typography sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                            No scrim games found for {teamLabel(userTeam)} in this period.
                        </Typography>
                    </NeonCardContent>
                </NeonCard>
            ) : (
                <>
                    {/* KPI strip */}
                    <NeonCard sx={{ height: "auto", flexShrink: 0 }}>
                        <NeonCardContent sx={{ py: 1.5, pb: "12px !important" }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(3, 1fr)",
                                        sm: "repeat(6, 1fr)",
                                    },
                                    gap: 1,
                                    width: "100%",
                                }}
                            >
                                <KpiTile label="Games" value={formatters.integer(kpis.games)} />
                                <KpiTile label="Wins" value={formatters.integer(kpis.wins)} accent="#81c784" />
                                <KpiTile label="Losses" value={formatters.integer(kpis.losses)} accent="#e57373" />
                                <KpiTile label="Win%" value={formatters.percent(kpis.winRate)} accent={neonColor} />
                                <KpiTile label="Maps" value={formatters.integer(kpis.maps)} />
                                <KpiTile label="Sessions" value={formatters.integer(kpis.sessions)} />
                            </Box>
                        </NeonCardContent>
                    </NeonCard>

                    {/* Left: sessions + players. Right: map stats fills remaining height */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                            gap: 2,
                            alignItems: "stretch",
                        }}
                    >
                        <Stack
                            spacing={2}
                            sx={{
                                minWidth: 0,
                                minHeight: 0,
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <NeonCard
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title={
                                        selectedMap
                                            ? `Recent games · ${capitalize(selectedMap)}`
                                            : "Recent games"
                                    }
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <NeonButton
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                                            onClick={() => navigate("/scrims-history")}
                                            sx={{ fontSize: "0.7rem", px: 1.25, py: 0.25 }}
                                        >
                                            History
                                        </NeonButton>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.75,
                                        pt: 0,
                                        pb: "12px !important",
                                        flex: 1,
                                        minHeight: 0,
                                        ...scrollable,
                                    }}
                                >
                                    {recentGames.map((game, index) => {
                                        const dayKey = dayjs(game.date).format("YYYY-MM-DD");
                                        const prevDayKey =
                                            index > 0
                                                ? dayjs(recentGames[index - 1].date).format("YYYY-MM-DD")
                                                : null;
                                        const showDayDivider = prevDayKey === null || prevDayKey !== dayKey;

                                        return (
                                            <React.Fragment key={game.id}>
                                                {showDayDivider && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            flexShrink: 0,
                                                            padding: "8px 12px",
                                                            marginTop: index === 0 ? 0 : 0.5,
                                                            borderRadius: "6px",
                                                            background: `linear-gradient(90deg, ${neonColor}12 0%, ${neonColor}06 30%, rgba(255,255,255,0.02) 100%)`,
                                                            borderLeft: `3px solid ${neonColor}`,
                                                            boxShadow: `inset 0 0 20px ${neonColor}08`,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.75rem",
                                                                fontWeight: 700,
                                                                color: "rgba(255,255,255,0.9)",
                                                                letterSpacing: "1px",
                                                            }}
                                                        >
                                                            {dayjs(game.date).format("ddd, MMM D")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                <GameRow
                                                    game={game}
                                                    userTeam={userTeam}
                                                    onOpen={() => navigate("/scrims-history")}
                                                    mapsById={mapsById}
                                                    breakdown={gameBreakdowns.get(game.id)}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                </NeonCardContent>
                            </NeonCard>

                            <NeonCard sx={{ height: "auto", minWidth: 0, flexShrink: 0 }}>
                                <NeonCardHeader
                                    title={
                                        selectedMap
                                            ? `Player stats · ${capitalize(selectedMap)}`
                                            : "Player stats"
                                    }
                                    sx={{ minHeight: 52, py: 1.25, flexShrink: 0 }}
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        width: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    {statsLoading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                            <CircularProgress size={28} sx={{ color: neonColor }} />
                                        </Box>
                                    ) : players.length === 0 ? (
                                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                            No player summaries available for these games.
                                        </Typography>
                                    ) : (
                                        <PlayerStatsTable players={players} />
                                    )}
                                </NeonCardContent>
                            </NeonCard>
                        </Stack>

                        <Box
                            sx={{
                                minWidth: 0,
                                minHeight: { xs: 360, md: 0 },
                                height: { xs: 360, md: "100%" },
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <NeonCard
                                sx={{
                                    height: "100%",
                                    minWidth: 0,
                                    minHeight: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <NeonCardHeader
                                    title="Map stats"
                                    sx={{ minHeight: "auto", py: 1.25, flexShrink: 0 }}
                                    action={
                                        <NeonButton
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                                            onClick={() => navigate("/scrims-map-stats")}
                                            sx={{ fontSize: "0.7rem", px: 1.25, py: 0.25 }}
                                        >
                                            Details
                                        </NeonButton>
                                    }
                                />
                                <NeonCardContent
                                    sx={{
                                        pt: 0,
                                        pb: "12px !important",
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: 0,
                                        overflow: "hidden",
                                    }}
                                >
                                    {mapStats.length === 0 ? (
                                        <Typography sx={{ color: "rgba(255,255,255,0.55)", py: 3, textAlign: "center" }}>
                                            No maps played in this period.
                                        </Typography>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: `repeat(${mapGrid.cols}, minmax(0, 1fr))`,
                                                gridTemplateRows: `repeat(${mapGrid.rows}, minmax(0, 1fr))`,
                                                gap: 0.75,
                                                flex: 1,
                                                minHeight: 0,
                                                height: "100%",
                                            }}
                                        >
                                            {mapStats.map((m) => (
                                                <MapStatCard
                                                    key={m.map}
                                                    stats={m}
                                                    neonColor={neonColor}
                                                    mapRef={mapsById.get(m.map.toLowerCase())}
                                                    selected={selectedMap?.toLowerCase() === m.map.toLowerCase()}
                                                    onSelect={() =>
                                                        setSelectedMap((prev) =>
                                                            prev?.toLowerCase() === m.map.toLowerCase()
                                                                ? null
                                                                : m.map,
                                                        )
                                                    }
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </NeonCardContent>
                            </NeonCard>
                        </Box>
                    </Box>
                </>
            )}
        </WindowContainer>
    );
}
